import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { emptyCampaign, emptySettlement } from "./campaignModel";
import { SaveQueue } from "./saveQueue";
const CampaignContext = createContext(null);

async function request(url, method = "GET", body) {
  const res = await fetch(url, {
    method, headers: body ? { "Content-Type": "application/json" } : {},
    ...(body ? { body: JSON.stringify(body), keepalive: new Blob([JSON.stringify(body)]).size < 60000 } : {})
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) window.dispatchEvent(new Event("kdm-auth-expired"));
  if (!res.ok) { const e = new Error(data.error || "Request failed"); e.status = res.status; throw e; }
  return data;
}
function hasPillar(pillars, name) {
  return (pillars || []).some(p => String(p).trim().toLowerCase() === name.toLowerCase());
}
export function CampaignProvider({ children }) {
  const [campaigns, setCampaigns] = useState([]);
  const [current, renderCurrent] = useState(emptyCampaign);
  const currentRef = useRef(current);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [saveStatus, setSaveStatus] = useState("Saved");
  const [isDraft, setIsDraft] = useState(false);
  const draftRef = useRef(false);
  const timer = useRef(null);
  const queueRef = useRef(null);
  if (!queueRef.current) queueRef.current = new SaveQueue(
    c => request("/api/campaigns/save", "POST", c), localStorage,
    (result, id) => {
      if (result && currentRef.current.id === id) {
        const next = { ...currentRef.current, revision: result.revision };
        currentRef.current = next; renderCurrent(next);
      }
      setSaveStatus(queueRef.current.pending.size ? "Unsaved changes" : "Saved");
    }
  );
  const queue = queueRef.current;
  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!queue.pending.size) return;
    setSaveStatus("Saving…");
    try { await queue.flush(); setError(""); setSaveStatus("Saved"); }
    catch (e) { setError(e.message); setSaveStatus("Not saved — download your recovery copy"); throw e; }
  }, [queue]);
  const setCurrent = useCallback(updater => {
    const prev = currentRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    if (next === prev) return;
    currentRef.current = next; renderCurrent(next);
    if (next.id && next.settlementName?.trim() && !draftRef.current) {
      try { queue.enqueue(next); }
      catch (e) { setError("Browser recovery storage failed: " + e.message); }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flush().catch(() => {}), 250);
    }
  }, [queue, flush]);
  const refreshCampaigns = async () => {
    try {
      const data = await request("/api/campaigns");
      setCampaigns(data.campaigns || []); setWarnings(data.warnings || []);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => {
    let active = true;
    try { queue.recover(); } catch (e) { setError("Recovery data could not be read: " + e.message); }
    flush().catch(() => {}).finally(() => { if (active) refreshCampaigns(); });
    const leave = () => flush().catch(() => {});
    const visibility = () => { if (document.visibilityState === "hidden") leave(); };
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      active = false; clearTimeout(timer.current);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [flush, queue]);
  const beginDraft = () => { draftRef.current = true; setIsDraft(true); };
  const commitDraft = () => { draftRef.current = false; setIsDraft(false); };
  const createNewCampaign = ({ settlementName, campaignType }) => {
    // Previous campaigns already have independent queued snapshots.
    const now = new Date().toISOString();
    const next = { ...emptyCampaign(), id: crypto.randomUUID(), settlementName, campaignType, createdAt: now, updatedAt: now };
    queue.remember(next); setCurrent(next);
  };
  const loadCampaign = async id => {
    try {
      // Do not silently discard an unsaved campaign on navigation.
      await flush();
      const data = await request("/api/campaigns/" + encodeURIComponent(id));
      const loaded = { ...emptyCampaign(), ...data.campaign,
        settlement: { ...emptySettlement(), ...data.campaign.settlement } };
      queue.remember(loaded);
      currentRef.current = loaded; renderCurrent(loaded);
      commitDraft(); setError("");
    } catch (e) { setError(e.message); throw e; }
  };
  const saveCampaign = async () => {
    if (!currentRef.current.settlementName?.trim()) throw Error("Settlement name is required");
    commitDraft(); queue.enqueue(currentRef.current);
    await flush(); await refreshCampaigns();
    return { id: currentRef.current.id };
  };
  const deleteCampaign = async id => {
    try {
      await flush();
      const c = await request("/api/campaigns/" + encodeURIComponent(id));
      await request("/api/campaigns/" + encodeURIComponent(id), "DELETE", { revision: c.campaign.revision });
      if (currentRef.current.id === id) { const empty = emptyCampaign(); currentRef.current = empty; renderCurrent(empty); }
      await refreshCampaigns();
    } catch (e) { setError(e.message); throw e; }
  };
  const updateSheetField = useCallback((survivorId, field, value, fallback) => {
    setCurrent(prev => {
      let changed = false;
      const survivors = prev.survivors.map(s => {
        if (s.id !== survivorId) return s;
        const before = field === "name" ? s.name : (s.sheet?.[field] ?? fallback);
        const next = typeof value === "function" ? value(before) : value;
        if (JSON.stringify(next) === JSON.stringify(before)) return s;
        changed = true;
        return { ...s, ...(field === "name" ? { name: next } : {}), sheet: { ...s.sheet, [field]: next } };
      });
      return changed ? { ...prev, survivors, updatedAt: new Date().toISOString() } : prev;
    });
  }, [setCurrent]);
  const updateCampaign = (patch) => {
    setCurrent((prev) => ({
      ...prev,
      ...patch,
      updatedAt: new Date().toISOString(),
    }));
  };

  const updateSettlement = (patch) => {
    setCurrent((prev) => ({
      ...prev,
      settlement: {
        ...emptySettlement(),
        ...(prev.settlement || {}),
        ...(patch || {}),
      },
      updatedAt: new Date().toISOString(),
    }));
  };

  // NEW: timeline progress writer (persistent)
  const updateTimelineProgress = (key, patch) => {
    setCurrent((prev) => {
      const prevTP = prev.timelineProgress && typeof prev.timelineProgress === "object"
        ? prev.timelineProgress
        : {};
      const prevEntry = prevTP[key] && typeof prevTP[key] === "object" ? prevTP[key] : {};
      const nextEntry = { ...prevEntry, ...(patch || {}) };

      return {
        ...prev,
        timelineProgress: { ...prevTP, [key]: nextEntry },
        updatedAt: new Date().toISOString(),
      };
    });
  };

  // Survivor helpers (unchanged)
  const addSurvivor = (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;

    const survivor = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      name: trimmed,
      notes: "",
      gear: [],
      borderColor: "",
    };

    setCurrent((prev) => ({
      ...prev,
      survivors: [...(prev.survivors || []), survivor],
      updatedAt: new Date().toISOString(),
    }));
  };

  const updateSurvivor = (survivorId, patch) => {
    setCurrent((prev) => ({
      ...prev,
      survivors: (prev.survivors || []).map((s) =>
        s.id === survivorId ? { ...s, ...patch, ...(patch.name !== undefined ? { sheet: { ...s.sheet, name: patch.name } } : {}) } : s
      ),
      updatedAt: new Date().toISOString(),
    }));
  };

  const removeSurvivor = (survivorId) => {
    setCurrent((prev) => {
      const nextDeparting = (prev.departingSurvivorIds || []).filter((id) => id !== survivorId);
      return {
        ...prev,
        survivors: (prev.survivors || []).filter((s) => s.id !== survivorId),
        departingSurvivorIds: nextDeparting,
        updatedAt: new Date().toISOString(),
      };
    });
  };

  const maxDeparting = () => {
    const pillars = current?.pillars || [];
    if (hasPillar(pillars, "scouts") || hasPillar(pillars, "scounts")) return 5;
    return 4;
  };

  const toggleDepartingSurvivor = (survivorId) => {
    setCurrent((prev) => {
      const existing = Array.isArray(prev.departingSurvivorIds) ? prev.departingSurvivorIds : [];
      const isOn = existing.includes(survivorId);

      if (isOn) {
        return {
          ...prev,
          departingSurvivorIds: existing.filter((id) => id !== survivorId),
          updatedAt: new Date().toISOString(),
        };
      }

      const limit = (() => {
        const pillars = prev?.pillars || [];
        if (hasPillar(pillars, "scouts") || hasPillar(pillars, "scounts")) return 5;
        return 4;
      })();

      if (existing.length >= limit) return prev;

      return {
        ...prev,
        departingSurvivorIds: [...existing, survivorId],
        updatedAt: new Date().toISOString(),
      };
    });
  };


  const downloadRecovery = () => {
    const entries = [...queue.pending.values()];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("kdm_outbox__") || key.startsWith("kdm_recovery__")) entries.push(JSON.parse(localStorage.getItem(key)));
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(entries, null, 2)], { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = "kingdom-death-recovery.json"; a.click(); URL.revokeObjectURL(url);
  };
  const loadLatest = async () => {
    const id = queue.blocked.keys().next().value || currentRef.current.id;
    // Explicit conflict resolution keeps a separate recovery copy, never overwrites the server.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key.startsWith("kdm_outbox__")) continue;
      const raw = localStorage.getItem(key);
      if (JSON.parse(raw)?.id !== id) continue;
      localStorage.setItem(key.replace("kdm_outbox__", "kdm_recovery__"), raw);
      localStorage.removeItem(key);
    }
    queue.pending.delete(id); queue.blocked.delete(id);
    await loadCampaign(id);
  };
  const value = { campaigns, current, error, warnings, saveStatus, isDraft, beginDraft, commitDraft,
    refreshCampaigns, createNewCampaign, loadCampaign, saveCampaign, deleteCampaign,
    updateCampaign, updateSettlement, updateTimelineProgress, updateSheetField,
    addSurvivor, updateSurvivor, removeSurvivor, toggleDepartingSurvivor, maxDeparting, flush, downloadRecovery, loadLatest };
  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
}
export function useCampaign() {
  const ctx = useContext(CampaignContext);
  if (!ctx) throw Error("useCampaign must be used inside CampaignProvider");
  return ctx;
}
